/**
 * Outgoing-HTML brand-color inject (Phase 1B #129).
 * Extracted from NewsletterAIService.js as part of #229 decomposition.
 *
 * Public exports:
 *   - applyBrandingToHtml({ html, userId }) — wraps HTML with the advisor's
 *     UserBranding.primaryColor as an inline style. No-op when html or
 *     userId is missing, or when branding/color is missing or invalid.
 *   - safeColor(input) — validates a colour string before HTML interpolation.
 *     Internal helper, exported for testability.
 */

import brandingService from '../BrandingService.js';

// Defense-in-depth: validate the colour before interpolating into HTML.
// Accepts:
//   #fff, #ffffff, #ffffffff (hex with optional alpha)
//   rgb(0,0,0), rgba(0,0,0,1), hsl(...), hsla(...)
//   named colours via a conservative allowlist (kept tiny — extend as needed)
// Anything else (e.g. attribute-breakout payloads) is rejected.
const SAFE_COLOR_RE = /^(?:#[0-9a-fA-F]{3,8}|rgba?\([\d\s,.%]+\)|hsla?\([\d\s,.%]+\))$/;
const SAFE_NAMED_COLORS = new Set(['black', 'white', 'red', 'blue', 'green', 'gray', 'grey']);

export function safeColor(input) {
  if (typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (!trimmed) return null;
  if (SAFE_COLOR_RE.test(trimmed)) return trimmed;
  if (SAFE_NAMED_COLORS.has(trimmed.toLowerCase())) return trimmed.toLowerCase();
  return null;
}

export async function applyBrandingToHtml({ html, userId } = {}) {
  if (!html) return html;
  if (!userId) return html;

  const branding = await brandingService.getUserBranding(userId);
  if (!branding || !branding.primaryColor) return html;

  const color = safeColor(branding.primaryColor);
  if (!color) return html; // reject anything that isn't a recognisable colour

  // Minimal inject — wrap the outgoing HTML in a div carrying the brand
  // colour as inline style (non-destructive; survives email clients that
  // strip <style> blocks).
  return `<div style="--brand-color:${color};color:${color}">${html}</div>`;
}
